/**
 * Importación **mayorista** por archivo (Excel) hacia el backend (solo flujo OV).
 *
 * Lo usa **`SalesOrderList.js`** (modal Carga Mayorista + POST). No confundir con `salesOrdersList.js`, que es
 * listado/búsqueda/detalle por API REST de órdenes ya existentes.
 *
 * - `uploadSalesOrdersWholesaleFile`: multipart + query params de cabecera (RUT, canal, marca, bodega, userId,
 *   `discountAmount` opcional en query (valor SAP `DiscountPercent`),
 *   `shippingMethod` opcional (`shipCode` del catálogo `GET shipping-methods`).
 * - `extractWholesaleImportCreatedCount`: cuántas OV creó el servidor (mensaje de éxito en `SalesOrderList`).
 */
import axiosInstance from '../utils/axiosInstance';

/**
 * Obtiene el número de OV/guías creadas desde respuestas habituales del backend (envelope o plano).
 *
 * @param {unknown} payload - `response.data` del POST import
 * @returns {number|null}
 */
export function extractWholesaleImportCreatedCount(payload) {
  if (payload == null || typeof payload !== 'object') return null;

  const asNum = (v) => {
    if (v == null || v === '') return null;
    if (typeof v === 'number' && Number.isFinite(v) && v >= 0) return Math.floor(v);
    const n = parseInt(String(v).replace(/\D/g, ''), 10);
    return Number.isFinite(n) && n >= 0 ? n : null;
  };

  let root = payload;
  if (root && typeof root === 'object' && root.code === 'SUCCESS' && root.data != null) {
    root = root.data;
  }
  if (root && typeof root === 'object' && root.records?.code === 'SUCCESS' && root.records.data != null) {
    root = root.records.data;
  }

  const inner = root && typeof root === 'object' ? root : {};

  const candidates = [
    inner.totalOrdersCreated,
    inner.createdOrdersCount,
    inner.ordersCreated,
    inner.ordenesCreadas,
    inner.guiasCreadas,
    inner.guidesCreated,
    inner.createdCount,
    inner.salesOrdersCreated,
    inner.numberOfOrdersCreated,
    inner.totalCreated,
    inner.count,
    inner.total,
    inner.data?.createdOrdersCount,
    inner.data?.ordersCreated,
    inner.data?.count,
    Array.isArray(inner.createdOrderIds) ? inner.createdOrderIds.length : null,
    Array.isArray(inner.createdOrders) ? inner.createdOrders.length : null,
    Array.isArray(inner.orders) ? inner.orders.length : null,
    Array.isArray(inner.salesOrders) ? inner.salesOrders.length : null,
    Array.isArray(inner.guias) ? inner.guias.length : null
  ];

  for (const c of candidates) {
    const n = asNum(c);
    if (n != null) return n;
  }
  return null;
}

/**
 * Desenvelopea el payload del POST import (misma lógica que `extractWholesaleImportCreatedCount`).
 * @param {unknown} payload
 * @returns {object}
 */
function unwrapWholesaleImportPayload(payload) {
  if (payload == null || typeof payload !== 'object') return {};
  let root = payload;
  if (root && typeof root === 'object' && root.code === 'SUCCESS' && root.data != null) {
    root = root.data;
  }
  if (root && typeof root === 'object' && root.records?.code === 'SUCCESS' && root.records.data != null) {
    root = root.records.data;
  }
  return root && typeof root === 'object' ? root : {};
}

/**
 * Datos de éxito del import mayorista (retailer, N° OV generado, etc.).
 *
 * @param {unknown} payload - `response.data` del POST import
 * @returns {{ retailer: string, retailerOrderNumber: string, totalOrdersCreated: number|null, firstOrderNumber: string }}
 */
export function extractWholesaleImportSuccessDetail(payload) {
  const inner = unwrapWholesaleImportPayload(payload);
  const retailer = String(inner.retailer ?? '').trim();
  const retailerOrderNumber = String(inner.retailerOrderNumber ?? inner.retailer_order_number ?? '').trim();
  const totalOrdersCreated = extractWholesaleImportCreatedCount(payload);
  const orders = Array.isArray(inner.orders) ? inner.orders : [];
  const first = orders[0];
  const firstOrderNumber =
    first && typeof first === 'object'
      ? String(first.orderNumber ?? first.order_number ?? '').trim()
      : '';
  return {
    retailer,
    retailerOrderNumber,
    totalOrdersCreated,
    firstOrderNumber: firstOrderNumber || retailerOrderNumber
  };
}

/**
 * Importación mayorista: POST multipart con archivo Excel y parámetros en query.
 * Base URL viene de REACT_APP_API_URL (incluye /api/v1/).
 * `canalvta`, `marca` y `tipovta` en `params` vienen de `SalesOrderList` con semántica de negocio:
 * `marca` = nombre de marca, `tipovta` = tipo de venta.
 * El endpoint de import invierte el mapeo respecto a los nombres del query (`marca` termina en sale_type_desc y
 * `tipovta` en brand en la respuesta); se cruzan al armar la URL para que queden bien en listado/API.
 *
 * `priceFile` opcional (solo Falabella Chile, archivo eOC con precios `COSTO_UNI` por SKU); el backend lo
 * acepta como parte multipart y hace el match SKU → COSTO_UNI lado servidor.
 *
 * `catalogItemCodes` opcional (Perú: Falabella PE / Oechsle / Ripley PE): mapa `Substitute → ItemCode` resuelto en
 * la vista previa contra AlternateCatNum. El backend lo aplica como PC13 (SKU IAC) de cada línea antes de crear la OV.
 *
 * @param {File} file
 * @param {{ rut: string, canalvta: string, marca: string, tipovta: string, warehouse: string|number, userId: string, discountAmount?: number|null, shippingMethod?: string|null, priceFile?: File|null, customerName?: string|null, catalogItemCodes?: Record<string, string>|null }} params
 * @returns {Promise<any>} response.data
 */
export async function uploadSalesOrdersWholesaleFile(file, params) {
  const {
    rut,
    canalvta,
    marca,
    tipovta,
    warehouse,
    userId,
    discountAmount,
    shippingMethod,
    priceFile,
    customerName,
    catalogItemCodes
  } = params;
  // Compensación contrato import: query `marca` ↔ `tipovta` respecto a brand / sale_type_desc en servidor.
  const queryMarca = String(tipovta);
  const queryTipovta = String(marca);

  const search = new URLSearchParams();
  search.set('rut', String(rut).trim());
  search.set('canalvta', String(canalvta));
  search.set('marca', queryMarca);
  search.set('tipovta', queryTipovta);
  search.set('warehouse', String(warehouse).trim());
  const uid = userId != null ? String(userId).trim() : '';
  if (uid) {
    search.set('userId', uid);
  }
  if (discountAmount != null && Number.isFinite(Number(discountAmount))) {
    search.set('discountAmount', String(Number(discountAmount)));
  }
  const sm = shippingMethod != null ? String(shippingMethod).trim() : '';
  if (sm) {
    search.set('shippingMethod', sm);
  }

  const formData = new FormData();
  formData.append('file', file);
  const cn = customerName != null ? String(customerName).trim() : '';
  if (cn) {
    formData.append('customerName', cn);
  }
  if (priceFile) {
    formData.append('priceFile', priceFile);
  }
  if (
    catalogItemCodes &&
    typeof catalogItemCodes === 'object' &&
    Object.keys(catalogItemCodes).length > 0
  ) {
    formData.append('catalogItemCodes', JSON.stringify(catalogItemCodes));
  }

  const url = `sales-orders-import/file-wholesales?${search.toString()}`;
  // Mismo criterio que otras subidas masivas en el proyecto; axios añade el boundary al multipart.
  const response = await axiosInstance.post(url, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
}

/**
 * Hace polling a GET /api/v1/sales-orders-import/jobs/{jobId} hasta recibir
 * SUCCESS o FAILED, o hasta agotar maxAttempts.
 *
 * @param {string} jobId
 * @param {{ intervalMs?: number, maxAttempts?: number, onStatusUpdate?: (status: string) => void }} options
 * @returns {Promise<object>} Resuelve con el payload del job (status SUCCESS).
 *                            Rechaza con Error si FAILED, timeout o job no encontrado.
 */
export async function pollWholesaleImportJob(jobId, options = {}) {
  const {
    intervalMs = 4000,
    maxAttempts = 75,
    onStatusUpdate = null,
  } = options;

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    await new Promise((resolve) => setTimeout(resolve, intervalMs));

    let response;
    try {
      response = await axiosInstance.get(`sales-orders-import/jobs/${jobId}`);
    } catch (err) {
      if (err?.response?.status === 404) {
        throw new Error(`Job ${jobId} no encontrado en el servidor.`);
      }
      // Errores de red transitorios (502, etc.): reintentar silenciosamente
      console.warn(`[pollWholesaleImportJob] intento=${attempt + 1} error de red:`, err?.message);
      continue;
    }

    const data = response.data;
    if (onStatusUpdate) onStatusUpdate(data?.status);

    if (data?.status === 'SUCCESS') return data;
    if (data?.status === 'FAILED') {
      throw new Error(data?.errorMessage || 'La importación falló en el servidor.');
    }
    // PENDING o PROCESSING → seguir esperando
  }

  throw new Error(
    `La importación tardó más de lo esperado (${Math.round((maxAttempts * intervalMs) / 60000)} min). ` +
    'Recargue la lista de OV para verificar si se crearon.'
  );
}
